import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Textarea, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Checkbox,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2, FiRefreshCw, FiStar, FiActivity } from 'react-icons/fi';
import api from '../api';
import EmailTestModal from './EmailTestModal';

export default function EmailAccountsModal({ isOpen, onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name:'', email_address:'', imap_host:'', imap_port:'993', imap_secure:true,
    smtp_host:'', smtp_port:'587', smtp_secure:true, username:'', password:'',
    signature_html:'', signature_text:''
  });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [testAccount, setTestAccount] = useState(null);
  const toast = useToast();

  const fetchAccounts = useCallback(async () => {
    try { const { data } = await api.get('/email/accounts'); setAccounts(data); } catch(e) {}
  }, []);

  useEffect(() => { if (isOpen) { fetchAccounts(); setShowForm(false); setEditingId(null); } }, [isOpen, fetchAccounts]);

  const resetForm = () => setForm({
    name:'', email_address:'', imap_host:'', imap_port:'993', imap_secure:true,
    smtp_host:'', smtp_port:'587', smtp_secure:true, username:'', password:'',
    signature_html:'', signature_text:''
  });
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: typeof e === 'boolean' ? e : e.target.value }));

  const handleSave = async () => {
    if (!form.email_address || !form.imap_host || !form.smtp_host || !form.username) {
      toast({ title: 'Fill required fields', status: 'warning', duration: 2000 }); return;
    }
    if (!editingId && !form.password) { toast({ title: 'Password required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingId && !payload.password) delete payload.password;
      if (editingId) { await api.put(`/email/accounts/${editingId}`, payload); }
      else { await api.post('/email/accounts', payload); }
      await fetchAccounts(); setShowForm(false); setEditingId(null); resetForm();
      toast({ title: 'Saved', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleSync = async (id) => {
    const acc = accounts.find(a => a.id === id);
    setSyncing(id);
    try {
      const { data } = await api.post(`/email/sync/${id}`);
      toast({ title: `Email Sync: ${acc?.email_address || ''}`, description: `${data.fetched} new, ${data.skipped} skipped`, status: 'success', duration: 3000 });
      await fetchAccounts();
    } catch(e) { toast({ title: `Email Sync Failed: ${acc?.email_address || ''}`, description: e.response?.data?.error || 'Connection error', status: 'error', duration: 4000 }); }
    finally { setSyncing(null); }
  };

  const openEdit = (a) => {
    setForm({ name: a.name, email_address: a.email_address, imap_host: a.imap_host || '',
      imap_port: String(a.imap_port || 993), imap_secure: a.imap_secure !== false,
      smtp_host: a.smtp_host || '', smtp_port: String(a.smtp_port || 587), smtp_secure: a.smtp_secure !== false,
      username: a.username || '', password: '', signature_html: a.signature_html || '', signature_text: a.signature_text || '' });
    setEditingId(a.id); setShowForm(true);
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>Email Accounts
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
              onClick={() => { resetForm(); setEditingId(null); setShowForm(!showForm); }}>{showForm ? 'Cancel' : 'Add'}</Button>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Display Name</FormLabel>
                    <Input size="xs" value={form.name} onChange={f('name')} placeholder="Sales Inbox" /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Email Address *</FormLabel>
                    <Input size="xs" value={form.email_address} onChange={f('email_address')} /></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>IMAP Host *</FormLabel>
                    <Input size="xs" value={form.imap_host} onChange={f('imap_host')} placeholder="imap.gmail.com" /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Port</FormLabel>
                    <Input size="xs" value={form.imap_port} onChange={f('imap_port')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>SSL</FormLabel>
                    <Checkbox size="sm" isChecked={form.imap_secure} onChange={e => setForm(p => ({ ...p, imap_secure: e.target.checked }))} mt={1}>SSL/TLS</Checkbox></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>SMTP Host *</FormLabel>
                    <Input size="xs" value={form.smtp_host} onChange={f('smtp_host')} placeholder="smtp.gmail.com" /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Port</FormLabel>
                    <Input size="xs" value={form.smtp_port} onChange={f('smtp_port')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>SSL</FormLabel>
                    <Checkbox size="sm" isChecked={form.smtp_secure} onChange={e => setForm(p => ({ ...p, smtp_secure: e.target.checked }))} mt={1}>SSL/TLS</Checkbox></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Username *</FormLabel>
                    <Input size="xs" value={form.username} onChange={f('username')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Password {editingId ? '(leave blank to keep)' : '*'}</FormLabel>
                    <Input size="xs" type="password" value={form.password} onChange={f('password')} /></FormControl>
                </HStack>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Email Signature (HTML)</FormLabel>
                  <Textarea size="xs" rows={3} value={form.signature_html} onChange={f('signature_html')} placeholder="<p>Regards,<br>Team</p>" /></FormControl>
                <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSave} isLoading={saving} alignSelf="flex-end">{editingId ? 'Update' : 'Add Account'}</Button>
              </VStack>
            </Box>
          )}
          {accounts.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>No email accounts configured.</Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {accounts.map(a => (
                <Flex key={a.id} p={3} border="1px" borderColor="gray.100" borderRadius="md" align="center" justify="space-between">
                  <Box>
                    <Flex align="center" gap={2}>
                      <Text fontSize="12px" fontWeight="600">{a.name || a.email_address}</Text>
                      <Badge fontSize="8px" colorScheme={a.is_active ? 'green' : 'red'}>{a.is_active ? 'Active' : 'Inactive'}</Badge>
                      {!!a.is_default && <Badge fontSize="8px" colorScheme="blue">Default</Badge>}
                    </Flex>
                    <Text fontSize="10px" color="gray.500">{a.email_address}</Text>
                    <Text fontSize="9px" color="gray.400">IMAP: {a.imap_host} · SMTP: {a.smtp_host}
                      {a.last_sync_at && ` · Last sync: ${new Date(a.last_sync_at).toLocaleString('en-GB')}`}</Text>
                  </Box>
                  <HStack spacing={1}>
                    <IconButton icon={<FiActivity />} size="xs" variant="ghost" colorScheme="teal"
                      onClick={() => setTestAccount(a)} aria-label="Test" title="Test connection" />
                    <IconButton icon={<FiStar />} size="xs" variant="ghost"
                      colorScheme={a.is_default ? 'yellow' : 'gray'}
                      onClick={async () => { await api.put(`/email/accounts/${a.id}`, { is_default: true }); fetchAccounts(); }}
                      aria-label="Set Default" title="Set as default sending address" />
                    <IconButton icon={<FiRefreshCw />} size="xs" variant="ghost" colorScheme="blue"
                      onClick={() => handleSync(a.id)} isLoading={syncing === a.id} aria-label="Sync" />
                    <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(a)} aria-label="Edit" />
                    <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                      onClick={async () => { await api.delete(`/email/accounts/${a.id}`); fetchAccounts(); }} aria-label="Delete" />
                  </HStack>
                </Flex>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
    <EmailTestModal
      isOpen={!!testAccount}
      onClose={() => setTestAccount(null)}
      account={testAccount}
    />
    </>
  );
}
